'use client'

import React, { useCallback, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { GET_CATEGORIES_TREE } from '../categoriesQueries'
import { useLanguage } from '@/context/LanguageContext'
import { CategoryTreeNode } from '../types'

interface CategoryTreeSelectProps {
  value: number | null | undefined
  onChange: (value: number | null) => void
  excludeId?: number
  placeholder?: string
}

export function CategoryTreeSelect({
  value,
  onChange,
  excludeId,
  placeholder = 'Select parent category...',
}: CategoryTreeSelectProps) {
  const [open, setOpen] = useState(false)
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const setTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    if (node) {
      setContainer(node.closest('[role="dialog"]') as HTMLElement | null)
    }
  }, [])
  const { language: currentLanguage } = useLanguage()

  const { data, loading } = useQuery(GET_CATEGORIES_TREE, {
    fetchPolicy: 'cache-and-network',
  })

  const categories: CategoryTreeNode[] = data?.getCategoriesAndChildsQuery || []

  // Filter out the category being edited and its descendants
  // to prevent circular parent-child relationships
  const getAvailableCategories = (): CategoryTreeNode[] => {
    if (!excludeId) return categories

    const excluded = new Set<number>()
    let excludeAtDepth: number | null = null

    for (const cat of categories) {
      if (cat.id === excludeId) {
        excluded.add(cat.id)
        excludeAtDepth = cat.depth_level
        continue
      }

      if (excludeAtDepth !== null) {
        if (cat.depth_level > excludeAtDepth) {
          excluded.add(cat.id)
          continue
        } else {
          excludeAtDepth = null
        }
      }
    }

    return categories.filter(c => !excluded.has(c.id))
  }

  const getName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      ''
    )
  }

  const selectedCategory = categories.find(c => c.id === value)
  const selectedName = selectedCategory
    ? getName(selectedCategory.category_profiles)
    : null

  const availableCategories = getAvailableCategories()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={setTriggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedName || placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {value && (
              <span
                role="button"
                className="inline-flex items-center justify-center"
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(null)
                  setOpen(false)
                }}
              >
                <X className="h-4 w-4 opacity-50 hover:opacity-100" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        container={container ?? undefined}
      >
        <Command>
          <CommandInput placeholder="Search categories..." />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading...' : 'No categories found.'}
            </CommandEmpty>
            <CommandGroup>
              {availableCategories.map(category => {
                const name = getName(category.category_profiles)
                const indent = category.depth_level * 16

                return (
                  <CommandItem
                    key={category.id}
                    value={`${category.id}-${name}`}
                    onSelect={() => {
                      onChange(category.id === value ? null : category.id)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === category.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span style={{ paddingLeft: indent }} className="truncate">
                      {name}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
